import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';

import { useQuery } from '@apollo/client/react';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AppBar as MLAppBar } from 'ml-react-component-library';
import { useLocation, useNavigate } from 'react-router';

import { GET_CURRENT_USER } from '../../../core/api/currentUserQuery';
import { shouldLogoutForAuthInvalidState } from '../../../core/auth/authInvalidState';

import AppHubMenu from './AppHubMenu';
import HelpMenu from './components/HelpMenu';
import type { AppBarProps } from './types';
import { getCurrentNavIndex, handlePortalHomeNavigation, resolvePortalAppBarTitle } from './utils';

/**
 * Shared AppBar used by every Client Matter Administration Portal app.
 * Each app renders its own instance so the title reflects the current app.
 * Pass `navItems` for in-app navigation links that appear centered in the bar.
 */
const AppBar = ({ title, navItems }: AppBarProps) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { instance, accounts, inProgress } = useMsal();
  const theme = useTheme();
  const showTitle = useMediaQuery(theme.breakpoints.up('lg'));
  const logoutInProgressRef = useRef(false);
  const account = instance.getActiveAccount() ?? accounts.at(0);
  const shouldFetchCurrentUser = account != null && inProgress === InteractionStatus.None;
  const { data, loading, error } = useQuery(GET_CURRENT_USER, {
    skip: !shouldFetchCurrentUser,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const currentUser = (data as { me?: unknown } | undefined)?.me;
  type MlAppBarUserData = NonNullable<NonNullable<Parameters<typeof MLAppBar>[0]['userProps']>['data']>;
  const appBarTitle = title ?? resolvePortalAppBarTitle(pathname);
  const [appHubOpen, setAppHubOpen] = useState(false);

  const currentPath = `${pathname}${search}`;
  const currentNavIndex = getCurrentNavIndex(currentPath, navItems);
  const navBar =
    navItems && navItems.length > 0
      ? {
          nav: navItems.map((item) => ({
            id: item.id,
            name: item.name,
            path: item.path,
            icon: item.icon,
            itemFunction: () => {
              void navigate(item.path);
            },
          })),
          currentValue: currentNavIndex >= 0 ? currentNavIndex : undefined,
        }
      : undefined;

  const handleLogin = () => {
    instance
      .loginRedirect({
        scopes: ['User.Read'],
        prompt: 'select_account',
      })
      .catch((e: unknown) => {
        console.error(e);
      });
  };

  const handleLogout = useCallback(() => {
    if (logoutInProgressRef.current) return;
    logoutInProgressRef.current = true;

    void instance
      .logoutRedirect({ postLogoutRedirectUri: globalThis.location?.origin ?? '' })
      .catch((logoutError: unknown) => {
        logoutInProgressRef.current = false;
        console.error(logoutError);
      });
  }, [instance]);

  useEffect(() => {
    if (!shouldFetchCurrentUser || loading || logoutInProgressRef.current) return;

    if (shouldLogoutForAuthInvalidState(error)) {
      handleLogout();
    }
  }, [error, handleLogout, loading, shouldFetchCurrentUser]);

  const extraActions = (
    <>
      <AppHubMenu open={appHubOpen} onOpenChange={setAppHubOpen} />
      <HelpMenu />
    </>
  );

  const userProps = {
    // Only show user menu when user profile data is present.
    // Prevents rendering "Sign in" fallback UI from the shared component library.
    displayUser: Boolean(currentUser),
    data: data as MlAppBarUserData,
    handleLogin,
    handleLogout,
  };

  const handleBrandClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      handlePortalHomeNavigation({ navigate, target: event.target });
    },
    [navigate],
  );

  return (
    <Box
      onClickCapture={handleBrandClick}
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        bgcolor: '#2e2d2e',
        '& img[alt="Morgan, Lewis & Bockius LLP Logo"]': {
          cursor: 'pointer',
        },
        '& .MuiAppBar-root': {
          width: '100%',
          minWidth: 0,
          left: 0,
          right: 0,
          backgroundColor: '#2e2d2e',
          color: '#fff',
        },
        '& .MuiAppBar-colorPrimary': {
          backgroundColor: '#2e2d2e',
          color: '#fff',
        },
        '& .MuiAppBar-root .MuiToolbar-root': {
          px: 'clamp(4px, 1.2vw, 12px)',
          minHeight: { xs: 56, sm: 63 },
          gap: 'clamp(2px, 0.6vw, 10px)',
        },
        '& .MuiAppBar-root .MuiIconButton-root': {
          p: 'clamp(3px, 0.7vw, 10px)',
        },
        '& .MuiAppBar-root .MuiIconButton-root svg': {
          fontSize: 'clamp(0.95rem, 1.5vw, 1.35rem)',
        },
        '& .MuiAppBar-root .MuiAvatar-root': {
          width: 'clamp(22px, 3vw, 32px)',
          height: 'clamp(22px, 3vw, 32px)',
        },
        '& .MuiAppBar-root .MuiStack-root': {
          minWidth: 0,
        },
        '& .MuiAppBar-root .MuiTabs-root': {
          minWidth: 0,
          flexShrink: 1,
        },
        '& .MuiAppBar-root .MuiTabs-scroller': {
          overflowX: 'auto !important',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
        '& .MuiAppBar-root .MuiTab-root': {
          minWidth: 'clamp(52px, 8vw, 78px)',
          px: 'clamp(2px, 0.6vw, 8px)',
          fontSize: 'clamp(0.62rem, 0.9vw, 0.75rem)',
        },
      }}
    >
      <MLAppBar
        title={showTitle ? appBarTitle : undefined}
        navBar={navBar}
        extraJSX={extraActions}
        userProps={userProps}
      />

      {/* <Breadcrumbs /> */}
    </Box>
  );
};

export type { AppBarProps } from './types';
export default AppBar;
